import React from 'react';
import type { TFunction } from 'i18next';
import moment from 'moment';

import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridColumn,
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { TKeys, useTranslation } from '../../../common/hooks/useTranslation';
import { GetEodImportLogDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import momencik from '../../../common/momencik';
import { PurchaseInvoiceLink } from '../PurchaseInvoices/PurchaseInvoiceLink';

import { EODJoinDocumentWithFVZLink } from './EODJoinDocumentWithFVZLink';

interface EodImportLogSearchProps {
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  refresh: boolean;
}

export const eodImportLogForGrid = (
  t: TFunction<'fvzLng', 'fvzLng'>,
  tk: TKeys<'fvzLng'>,
  importLog: GetEodImportLogDto,
  refresh: boolean,
  setRefresh: (refresh: boolean) => void,
) => {
  return {
    key: importLog.id.toString(),
    values: {
      'log.documentId': importLog.documentId?.toString(),
      'log.documentExternalNumber': (
        <>
          {importLog.documentExternalNumber || ''}
          {/* {importLog.documentId && !importLog.purchaseInvoiceId && (
            <EODReimportLink
              documentId={importLog.documentId}
              refresh={refresh}
              setRefresh={setRefresh}
            />
          )} */}
        </>
      ),
      'log.documentIssueDate': importLog.documentIssueDate
        ? momencik(importLog.documentIssueDate)
        : '',
      'log.reason': importLog.reason,
      'log.description': importLog.description || '',
      'log.creationTs': moment(importLog.creationTs)
        .locale(t(tk.common.localeShort))
        .format('LLL'),
      'log.company': importLog.company?.name || '',
      'purchaseInvoice.number': (
        <>
          {importLog.purchaseInvoice?.number || ''}
          {!importLog.purchaseInvoice?.id && importLog.documentId && (
            <EODJoinDocumentWithFVZLink
              eodId={importLog.documentId}
              onRefresh={() => setRefresh(!refresh)}
            />
          )}
        </>
      ),
      'purchaseInvoice.systemNumber': (
        <>
          {importLog.purchaseInvoice?.systemNumber || ''}
          {importLog.purchaseInvoice?.id && (
            <PurchaseInvoiceLink
              purchaseInvoiceId={importLog.purchaseInvoice?.id}
            />
          )}
        </>
      ),
    },
  };
};

export const EodImportLogSearch: React.FC<EodImportLogSearchProps> = props => {
  const { t, tk } = useTranslation('fvzLng');

  const columns: GridColumn[] = [
    {
      header: t(tk.eod.documentExternalNumber),
      property: 'log.documentExternalNumber',
    },
    {
      header: t(tk.eod.documentIssueDate),
      property: 'log.documentIssueDate',
    },
    {
      header: t(tk.eod.reason),
      property: 'log.reason',
    },
    {
      header: t(tk.eod.description),
      property: 'log.description',
    },
    {
      header: t(tk.eod.purchaseInvoiceNumber),
      property: 'purchaseInvoice.number',
    },
    {
      header: t(tk.eod.purchaseInvoiceSystemNumber),
      property: 'purchaseInvoice.systemNumber',
    },
    {
      header: t(tk.common.modificationDate),
      property: 'log.creationTs',
    },
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      showFilter={true}
      initialOrderColumn="log.creationTs"
      initialOrderDirection="DESC"
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage={t(tk.eod.noImportLogs)}
      withHistory={true}
      refresh={props.refresh}
    />
  );
};
